import { NavigationContainer, getPathFromState as defaultGetPathFromState, getStateFromPath as defaultGetStateFromPath, } from '@react-navigation/native';
import { Provider } from 'react-redux';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { PersistGate } from 'redux-persist/integration/react';
import { ReducedMotionConfig, ReduceMotion } from 'react-native-reanimated';
import Modal from 'design-system/ModalWeb';
import AlertModalWeb from 'design-system/AlertModalWeb';
import PopUpMenuModal from 'design-system/PopUpMenuModal.web';
import WebNavigationBar from 'design-system/WebNavigationBar';
import WebStack from 'navigators/WebNavigator';
import configureStore from 'store/configureStore';
import { navigationRef } from 'utils/navigationv6';
import { getDocumentTitle } from 'utils/webDocumentTitle';
import { setCurrentRouteName } from 'actions/utils';
import { getDuplicateReviewPath } from 'features/subscriptions/utils/duplicateReview';
import { linking as mobileAppLinking, parseId } from 'hooks/useAppLink';
import { useJoinSpaceRedirect } from 'hooks/useJoinSpaceRedirect';
import SpaceUnavailableLock from 'features/lock/spaces/SpaceUnavailableLock';
import StatusBarScrambled from 'common/StatusBarScrambled';
import CreditReportUntrackedToast from 'features/credit-score/components/CreditReportUntrackedToast';
import ErrorModal from './common/ErrorModal';
const { store, persistor } = configureStore();
const AuthRedirectWrapper = () => {
    useJoinSpaceRedirect();
    return null;
};
const SAVE_DETAIL_PATHS = {
    Pot: '/save/pot',
    PotsActivity: '/save/pot/activity',
    EditPotGoal: '/save/pot/goal',
    PotInterestRates: '/save/interest-rates',
    InterestPotsFAQs: '/save/faqs',
    AutosaveActivityTimeline: '/save/autosave-activity',
    DepositTimeline: '/save/deposit',
    WithdrawTimeline: '/save/withdrawal',
    CurrentAccountSwitchOffer: '/save/switch-current-account/offer',
    BroadBandQuotes: '/save/broadband',
    BroadbandQuote: '/save/broadband/quote',
    PostCode: '/save/broadband/postcode',
    EnergyQuotes: '/save/energy',
    InformationLabel: '/save/switching/information',
    PersonalProjection: '/save/switching/projection',
    Quote: '/save/switching/quote',
    QuoteFurtherInformation: '/save/switching/quote/information',
    SwitchCompleted: '/save/switching/completed',
};
// Invest detail screens take objects and callbacks as route params (an order,
// onConfirm), which a URL can't carry, so each gets an invest/... path that
// reopens the Invest tab.
const INVEST_DETAIL_PATHS = {
    InvestPortfolio: '/invest/portfolio',
    InvestStock: '/invest/asset',
    InvestDiscover: '/invest/discover',
    InvestSearchStock: '/invest/search',
    InvestStaticCollectionDetails: '/invest/collection',
    InvestDiscoverCategorizationDetails: '/invest/discover/category',
    InvestCategoryFilterDetailsScreen: '/invest/discover/filter',
    InvestStockNews: '/invest/asset/news',
    InvestFinancial: '/invest/asset/financials',
    InvestAnalystRatingsDetails: '/invest/asset/analyst-ratings',
    InvestCorporateEvents: '/invest/asset/corporate-events',
    InvestFundAllocationDetail: '/invest/asset/allocation',
    PerformanceExample: '/invest/performance-example',
    FXInfoScreen: '/invest/fx-info',
    InvestWatchList: '/invest/watch-list',
    InvestCreateWatchList: '/invest/watch-list/new',
    InvestStockAlertScreen: '/invest/asset/alerts',
    InvestSetStockAlertScreen: '/invest/asset/alerts/new',
    InvestActivity: '/invest/activity',
    InvestNewActivityScreen: '/invest/activity/item',
    InvestInsights: '/invest/insights',
    StartInvestChooseAccount: '/invest/start/account',
    StartInvestChooseStrategy: '/invest/start/strategy',
    StartInvestReadyMadeFunds: '/invest/start/ready-made-funds',
    Pdf: '/invest/document',
    DepositToTradingAccountTimeline: '/invest/deposit',
    DepositAndBuyAssetTimeline: '/invest/deposit-and-buy',
    WithdrawFromAccountTimeline: '/invest/withdrawal',
    AutoinvestManage: '/invest/autoinvest',
    AutoinvestActivities: '/invest/autoinvest/activity',
    AutoinvestActivityTimeline: '/invest/autoinvest/payment',
    ProductTransferProgress: '/invest/transfer',
    InvestBuyCustomAmount: '/invest/buy',
    SelectAssetFromPositions: '/invest/buy/select',
    BuyAssetForTradingAccount: '/invest/buy/amount',
    DepositSelectCustomAmount: '/invest/buy/custom-amount',
    SecclOrderPreviewScreen: '/invest/order-preview',
    InvestOrder: '/invest/order',
    BeforeYouSell: '/invest/sell/before-you-sell',
    InvestSell: '/invest/sell',
    SelectAssetToSell: '/invest/sell/select',
    SellAssetFromTradingAccount: '/invest/sell/amount',
    InvestAssetSwitch: '/invest/switch',
    SecclAssetSwitchOrderPreview: '/invest/switch/preview',
    InvestSwitchOrder: '/invest/switch/order',
    InvestSwitchOrderDetails: '/invest/switch/order/details',
    InvestOrders: '/invest/orders',
    InvestReceipt: '/invest/receipt',
    InvestSettings: '/invest/settings',
    PositionsInfo: '/invest/settings/positions',
    BuyBlockScreen: '/invest/settings/buy-block',
    JISAChildDetails: '/invest/settings/child',
    ExportStatement: '/invest/statements/export',
    ValuationStatement: '/invest/statements/valuation',
    AnnualCostsAndCharges: '/invest/statements/costs-and-charges',
    SelectCustomPeriodForStatements: '/invest/statements/period',
};
// Groups and bill splitting. Group screens other than the group itself take
// objects and callbacks as route params (a group, onContinue), which a URL
// can't carry, so each gets a groups/... path that reopens the groups list.
// The paths have two segments so they don't match groups/:id.
const GROUP_DETAIL_PATHS = {
    GroupsIntro: '/groups/new/intro',
    GroupCreate: '/groups/new/name',
    GroupInvite: '/groups/new/members',
    AddToGroup: '/groups/add/select',
    GroupSelectTransactions: '/groups/add/transactions',
    GroupExpense: '/groups/add/expense',
    GroupCustomSplit: '/groups/add/split',
    GroupSettings: '/groups/group/settings',
    GroupTransactions: '/groups/group/transactions',
    GroupTransaction: '/groups/group/transaction',
    GroupSettleUp: '/groups/settle/select',
    GroupSettleOffline: '/groups/settle/record',
};
const getTransactionRoutePath = (state, routeName = 'Item') => {
    const transactionRoute = state.routes.find((route) => route.name === routeName);
    if (transactionRoute?.path) {
        return transactionRoute?.path;
    }
    return '';
};
const linking = {
    prefixes: [],
    config: {
        screens: {
            Feed: {
                path: '',
            },
            Analytics: mobileAppLinking.config?.screens.Analytics,
            Budgeting: mobileAppLinking.config?.screens.Budgeting,
            Support: mobileAppLinking.config?.screens.Support,
            AccountsOverview: mobileAppLinking.config?.screens.AccountsOverview,
            Subscriptions: mobileAppLinking.config?.screens.Subscriptions,
            Transactions: mobileAppLinking.config?.screens.Transactions,
            CommittedList: mobileAppLinking.config?.screens.CommittedList,
            NotificationsList: mobileAppLinking.config?.screens.NotificationsList,
            DailyBalanceNotifications: mobileAppLinking.config?.screens.DailyBalanceNotifications,
            TransactionsList: mobileAppLinking.config?.screens.TransactionsList,
            ScheduledTransactionList: mobileAppLinking.config?.screens.ScheduledTransactionList,
            Item: mobileAppLinking.config?.screens.Item,
            Category: mobileAppLinking.config?.screens.Category,
            MonthlyCategory: mobileAppLinking.config?.screens.MonthlyCategory,
            InactivePayments: mobileAppLinking.config?.screens.InactivePayments,
            Subscription: mobileAppLinking.config?.screens.Subscription,
            RenameSubscription: {
                path: 'subscriptions/:id/rename',
                parse: parseId,
            },
            AdjustSubscription: {
                path: 'subscriptions/:id/edit',
                parse: { ...parseId, activate: (s) => s === 'true' },
            },
            AdjustPrediction: {
                path: 'subscriptions/:id/adjust-prediction',
                parse: { ...parseId, isCreate: (s) => s === 'true' },
            },
            // The duplicate review's screens keep their state in route params, which
            // a URL can't carry (see getPathFromState). Reloading any of their paths
            // starts the review again: every group, or one subscription's group.
            DuplicateReview: {
                path: 'subscriptions/duplicates',
                alias: [
                    {
                        path: 'subscriptions/:subscriptionIds/duplicates',
                        parse: { subscriptionIds: (id) => [parseInt(id, 10)] },
                    },
                    'subscriptions/duplicates/keep',
                    'subscriptions/duplicates/cancel',
                    'subscriptions/duplicates/cancelled',
                ],
            },
            AccountSettings: mobileAppLinking.config?.screens.AccountSettings,
            AccountSettingsList: mobileAppLinking.config?.screens.AccountSettingsList,
            PreferenceSettingsList: mobileAppLinking.config?.screens.PreferenceSettingsList,
            AboutUs: mobileAppLinking.config?.screens.AboutUs,
            ImportantStuff: mobileAppLinking.config?.screens.ImportantStuff,
            PersonalDetails: mobileAppLinking.config?.screens.PersonalDetails,
            ChangePasscode: mobileAppLinking.config?.screens.ChangePasscode,
            Learn: mobileAppLinking.config?.screens.Learn,
            Quests: mobileAppLinking.config?.screens.Quests,
            PlanOverview: mobileAppLinking.config?.screens.PlanOverview,
            Notifications: mobileAppLinking.config?.screens.Notifications,
            HiddenAccountsOverview: {
                path: 'accounts/hidden',
            },
            ClosedAccountsOverview: {
                path: 'accounts/closed',
            },
            RenameTransaction: {
                path: 'transactions/:id/rename',
                parse: parseId,
            },
            EditDate: {
                path: 'transactions/:id/edit-date',
                parse: parseId,
            },
            AddNote: {
                path: 'transactions/:id/add-note',
                parse: parseId,
            },
            SplitTransaction: {
                path: 'transactions/:id/split',
                parse: parseId,
            },
            SuggestMerchantOptions: mobileAppLinking.config?.screens.SuggestMerchantOptions,
            MerchantFeedback: mobileAppLinking.config?.screens.MerchantFeedback,
            ConfirmMerchant: mobileAppLinking.config?.screens.ConfirmMerchant,
            PreviewMerchantOverride: mobileAppLinking.config?.screens.PreviewMerchantOverride,
            SearchTransactions: mobileAppLinking.config?.screens.SearchTransactions,
            EditAccountName: mobileAppLinking.config?.screens.EditAccountName,
            EditOverdraft: mobileAppLinking.config?.screens.EditOverdraft,
            SavingGoalEdit: mobileAppLinking.config?.screens.SavingGoalEdit,
            AnalyticsIntro: mobileAppLinking.config?.screens.AnalyticsIntro,
            SelectMerchant: mobileAppLinking.config?.screens.SelectMerchant,
            EditMerchantBudgets: mobileAppLinking.config?.screens.EditMerchantBudgets,
            EditMerchantBudget: mobileAppLinking.config?.screens.EditMerchantBudget,
            UnbudgetedSpend: mobileAppLinking.config?.screens.UnbudgetedSpend,
            DummyAccountSetup: mobileAppLinking.config?.screens.DummyAccountSetup,
            DummyAccountCreated: mobileAppLinking.config?.screens.DummyAccountCreated,
            DummyAccountLogo: mobileAppLinking.config?.screens.DummyAccountLogo,
            DummyAccountLogoType: mobileAppLinking.config?.screens.DummyAccountLogoType,
            DummyAccountCreationImport: mobileAppLinking.config?.screens.DummyAccountCreationImport,
            HasValuationChanged: mobileAppLinking.config?.screens.HasValuationChanged,
            AddBalance: mobileAppLinking.config?.screens.AddBalance,
            AddAmount: mobileAppLinking.config?.screens.AddAmount,
            AddValuation: mobileAppLinking.config?.screens.AddValuation,
            AddAnEmoji: mobileAppLinking.config?.screens.AddAnEmoji,
            OfflineAccounts: mobileAppLinking.config?.screens.OfflineAccounts,
            PickAccountType: mobileAppLinking.config?.screens.PickAccountType,
            Property: mobileAppLinking.config?.screens.Property,
            MySpaces: mobileAppLinking.config?.screens.MySpaces,
            FindSpaceAccounts: {
                path: 'find-space-accounts',
            },
            SpaceUnavailable: {
                path: 'space-unavailable',
            },
            JoinSpace: mobileAppLinking.config?.screens.JoinSpace,
            Borrow: {
                path: 'borrow',
            },
            // Save screens whose params hold whole objects (a pot, a quote) can't be
            // rebuilt from a URL, so getPathFromState gives them a save/... path
            // that reopens the Save tab.
            Save: {
                path: 'save',
                alias: Object.values(SAVE_DETAIL_PATHS).map((path) => path.slice(1)),
            },
            PotList: mobileAppLinking.config?.screens.PotList,
            ArchivedPots: { path: 'pots/archived' },
            AutoSave: mobileAppLinking.config?.screens.AutoSave,
            AutosaveActivities: { path: 'auto-save/activity' },
            PensionBee: mobileAppLinking.config?.screens.PensionBee,
            CurrentAccountSwitchOffers: { path: 'switch-current-account' },
            Invest: {
                path: 'invest',
                alias: Object.values(INVEST_DETAIL_PATHS).map((path) => path.slice(1)),
            },
            // Groups and bill splitting
            GroupsList: {
                path: 'groups',
                alias: Object.values(GROUP_DETAIL_PATHS).map((path) => path.slice(1)),
            },
            Group: mobileAppLinking.config?.screens.Group,
            GroupJoin: mobileAppLinking.config?.screens.GroupJoin,
            NotFound: '*',
            AuthComplete: mobileAppLinking.config?.screens.AuthComplete,
            Logins: mobileAppLinking.config?.screens.Logins,
            BankConnection: mobileAppLinking.config?.screens.BankConnection,
            ClosedAccounts: mobileAppLinking.config?.screens.ClosedAccounts,
            LoginsReconsent: {
                path: 'logins-reconsent',
            },
            Reward: {
                path: 'reward',
                parse: parseId,
            },
            Rewards: {
                path: 'cashback',
            },
            RewardCategory: {
                path: 'reward-category',
            },
            ClaimRewards: {
                path: 'claim-rewards',
            },
            ClaimRewardsDetails: {
                path: 'claim-rewards-details',
            },
            InviteScreen: mobileAppLinking.config?.screens.InviteScreen,
            ReferralPayout: mobileAppLinking.config?.screens.ReferralPayout,
            CreditReferralDetails: {
                path: 'referral',
                parse: parseId,
            },
            ClaimEmail: {
                path: 'claim-email',
            },
            ConfirmClaimEmail: {
                path: 'confirm-claim-email',
            },
            ClaimRewardCompleted: {
                path: 'claim-reward-completed',
            },
            NetworthTab: {
                path: 'networth-tab',
            },
            RecurringTab: {
                path: 'recurring-tab',
            },
            BudgetingTab: {
                path: 'budgeting-tab',
            },
            Help: {
                path: 'help/:articlesKey?',
            },
            SearchHelpArticles: {
                path: 'help-search',
                parse: {
                    searchBarY: (searchBarY) => parseInt(searchBarY, 10),
                },
            },
            ExportData: mobileAppLinking.config?.screens.ExportData,
            ExportDataLive: mobileAppLinking.config?.screens.ExportDataLive,
            ExportDataLiveTransit: mobileAppLinking.config?.screens.ExportDataLiveTransit,
            ExportDataLiveRetry: mobileAppLinking.config?.screens.ExportDataLiveRetry,
            CreditScoreEnhancedCreditFactors: {
                path: 'credit-score/enhanced-factors',
            },
            CreditReportAccountsScreen: {
                path: 'credit-report/accounts',
            },
            CreditScoreHistoryScreen: {
                path: 'credit-score/history',
            },
            CreditReportAccountDetailScreen: {
                path: 'credit-report/accounts/details',
                parse: {
                    accountId: (accountId) => parseInt(accountId, 10),
                },
            },
            CreditReportClosedAccountsScreen: {
                path: 'credit-report/accounts/closed',
            },
            CreditReportIdentityScreen: {
                path: 'credit-report/identity',
            },
            CreditReportIdentityDetailScreen: {
                path: 'credit-report/identity/:type',
            },
            CreditReportRulingsScreen: {
                path: 'credit-report/rulings',
            },
            CreditReportRulingDetailScreen: {
                path: 'credit-report/rulings/:type',
            },
            CreditReportSearchScreen: {
                path: 'credit-report/search',
                parse: {
                    isSoft: (isSoft) => isSoft === 'true',
                },
            },
            CreditReportSearchDetailScreen: {
                path: 'credit-report/search/details',
            },
            CreditReportDispute: {
                path: 'credit-report/dispute',
                parse: {
                    accountId: (accountId) => parseInt(accountId, 10),
                },
            },
        },
    },
    getStateFromPath(path, options) {
        // CreditReferralDetails needs a numeric id, so /referral links without one are
        // sent to NotFound rather than opening a screen with nothing to show.
        const [pathname, query = ''] = path.split('?');
        if (pathname === '/referral' &&
            !/^\d+$/.test(new URLSearchParams(query).get('id') ?? '')) {
            return { routes: [{ name: 'NotFound', path }] };
        }
        // Group needs a numeric id, so /groups/<name> links are sent to NotFound.
        if (/^\/groups\/[^/]+$/.test(pathname) &&
            !/^\/groups\/\d+$/.test(pathname)) {
            return { routes: [{ name: 'NotFound', path }] };
        }
        if (path.startsWith('/offline-accounts/') &&
            [
                'isChangingBalance=true',
                'isChangingName=true',
                'isChangingLogo=true',
            ].some((omitPath) => path.includes(omitPath))) {
            return { routes: [{ name: 'NotFound', path }] };
        }
        return defaultGetStateFromPath(path, options);
    },
    getPathFromState(state, options) {
        // Offers are opened with the whole reward object, which a URL can't carry,
        // so the URL gets its id and RewardScreen fetches the offer after a reload.
        // After a reload the offer is the only route, so this sits outside the check below.
        const focusedRoute = state?.routes?.[state.routes.length - 1];
        if (focusedRoute?.name === 'Reward') {
            const params = focusedRoute.params;
            const id = params && 'reward' in params ? params.reward?.id : params?.id;
            if (id) {
                return `/reward?id=${id}`;
            }
        }
        if (state?.routes && state?.index && state?.routes.length > 1) {
            const currentRoute = state.routes[state.routes.length - 1];
            if (currentRoute.name === 'SuccessSplit' ||
                currentRoute.name === 'Success') {
                return '/success';
            }
            const saveDetailPath = SAVE_DETAIL_PATHS[currentRoute.name];
            if (saveDetailPath) {
                return saveDetailPath;
            }
            const investDetailPath = INVEST_DETAIL_PATHS[currentRoute.name];
            if (investDetailPath) {
                return investDetailPath;
            }
            const groupDetailPath = GROUP_DETAIL_PATHS[currentRoute.name];
            if (groupDetailPath) {
                return groupDetailPath;
            }
            const duplicatePath = getDuplicateReviewPath(currentRoute);
            if (duplicatePath) {
                return duplicatePath;
            }
            if (currentRoute.name === 'RenameTransaction' &&
                !currentRoute.params?.id) {
                return 'transactions?edit-name=true';
            }
            if (currentRoute.name === 'AddNote' &&
                !currentRoute.params?.id) {
                return 'transactions?edit-note=true';
            }
            if (currentRoute.name === 'EditDate' &&
                !currentRoute.params?.id) {
                return 'transactions?edit-date=true';
            }
            if (currentRoute.name === 'AddTags') {
                const basePath = getTransactionRoutePath(state);
                return `${basePath}?edit-tags=true`;
            }
            if (currentRoute.name === 'EditMultipleTransactionsTags') {
                const basePath = getTransactionRoutePath(state);
                return `${basePath}?edit-tags=true&selectTransactions=true`;
            }
            if (currentRoute.name === 'EditMultipleTransactionsRename') {
                const basePath = getTransactionRoutePath(state, 'EditMultipleTransactionsRename');
                return `${basePath}?select-transactions=true`;
            }
            if (currentRoute.name === 'LearningScreen') {
                const basePath = getTransactionRoutePath(state);
                return `${basePath}?success=true`;
            }
            if (currentRoute.name === 'EditCategory') {
                return `/pick-category`;
            }
            if (currentRoute.name === 'EditMultipleTransactions') {
                return `/pick-category?select-transactions=true`;
            }
            if (currentRoute.name === 'ManageCategories') {
                return `/manage-categories`;
            }
            if (currentRoute.name === 'ReorderCategories') {
                return `/reorder-categories`;
            }
            if (currentRoute.name === 'CustomCategoryEdit') {
                return `/manage-categories/edit`;
            }
            if (currentRoute.name === 'CustomCategoryCreated') {
                return `/manage-categories/created`;
            }
            if (currentRoute.name === 'SubscriptionsSearch') {
                return '/search/recurring-payment';
            }
            if (currentRoute.name === 'AdjustPrediction') {
                const routeParams = currentRoute.params;
                const base = `/subscriptions/${routeParams.id}/adjust-prediction`;
                return routeParams.isCreate ? `${base}?isCreate=true` : base;
            }
            if (currentRoute.name === 'RenameSubscription') {
                const routeParams = currentRoute.params;
                if (routeParams?.id) {
                    return `/subscriptions/${routeParams.id}/rename`;
                }
                return '/recurring-payment/rename';
            }
            if (currentRoute.name === 'AdjustSubscription') {
                const routeParams = currentRoute.params;
                if (routeParams?.id) {
                    const base = `/subscriptions/${routeParams.id}/edit`;
                    return routeParams.activate ? `${base}?activate=true` : base;
                }
                return '/recurring-payment/add';
            }
            if (currentRoute.name === 'SearchTransactions') {
                return '/search-transactions';
            }
            if (currentRoute.name === 'OneMillionNetWorth') {
                return '/one-million-net-worth';
            }
            if (currentRoute.name === 'EditBudgetFrequency') {
                return '/select-budgeting-frequency';
            }
            if (currentRoute.name === 'HolidaySelection') {
                return '/select-budgeting-frequency';
            }
            if (currentRoute.name === 'SelectDate') {
                return '/select-date';
            }
            if (currentRoute.name === 'SelectPeriod') {
                return '/select-budgeting-period';
            }
            if (currentRoute.name === 'SetUpBudget') {
                return '/set-up-budget';
            }
            if (currentRoute.name === 'ProcessAutoSetUpBudget') {
                return '/setting-up-budget';
            }
            if (currentRoute.name === 'EditBudget') {
                const routeParams = currentRoute.params;
                if (routeParams.flow === 'Set') {
                    return '/set-budget';
                }
                if (routeParams.flow === 'Confirm') {
                    return '/confirm-budget';
                }
                return '/edit-budget';
            }
            if (currentRoute.name === 'Rollover') {
                return '/set-rollover';
            }
            if (currentRoute.name === 'Budgets') {
                const routeParams = currentRoute.params;
                if (routeParams.flow === 'Set') {
                    return '/set-category-budgets';
                }
                if (routeParams.flow === 'Confirm') {
                    return '/confirm-category-budgets';
                }
                return '/edit-category-budget';
            }
            if (currentRoute.name === 'ExcludeCategories') {
                return '/exclude-categories';
            }
            if (currentRoute.name === 'TrueBalanceIntro') {
                return '/true-balance-intro';
            }
            if (currentRoute.name === 'TrueBalance') {
                // Adding there as this shouldnt be accessible to everyone
                return '/true-balance';
            }
            if (currentRoute.name === 'JoinedSpace') {
                return '/joined-space';
            }
            if (currentRoute.name === 'EditSpace') {
                return '/edit-space';
            }
            if (currentRoute.name === 'CreateSpaceName') {
                return '/create-space/name';
            }
            if (currentRoute.name === 'EditSpaceAccounts') {
                return '/edit-space/accounts';
            }
            if (currentRoute.name === 'CreateSpaceAccounts') {
                return '/space/accounts';
            }
            if (currentRoute.name === 'CreateSpaceFinished') {
                return '/create-space-finished';
            }
            if (currentRoute.name === 'PrimarySpaceAccounts') {
                return '/primary-space-accounts';
            }
            if (currentRoute.name === 'JoinSpace') {
                const routeParams = currentRoute.params;
                const accessCode = routeParams?.accessCode;
                const spaceName = routeParams?.spaceName;
                if (accessCode) {
                    const spaceNameParam = spaceName
                        ? `?spaceName=${encodeURIComponent(spaceName)}`
                        : '';
                    return `/join-space/${accessCode}${spaceNameParam}`;
                }
            }
            if (currentRoute.name === 'InviteToSpace') {
                return '/invite-members';
            }
            if (currentRoute.name === 'SpaceUnavailableMoveAccounts') {
                return '/space-unavailable/move-accounts';
            }
            if (currentRoute.name === 'SpaceUnavailableSelectMoveAccounts') {
                return '/space-unavailable/select-move-accounts';
            }
            if (currentRoute.name === 'SpaceUnavailableAccountsMoved') {
                return '/space-unavailable/accounts-moved';
            }
            if (currentRoute.name === 'SpaceUnavailableLeaveAccounts') {
                return '/space-unavailable/accounts-left';
            }
            if (currentRoute.name === 'ManageSpaces') {
                return '/manage-spaces';
            }
            if (currentRoute.name === 'ManageSpaceInvite') {
                return '/manage-space-invite';
            }
            if (currentRoute.name === 'SpaceInvite') {
                return '/space-invite';
            }
            if (currentRoute.name === 'SpacesIntro') {
                return '/spaces-intro';
            }
            if (currentRoute.name === 'AllSpaceAccountsDraggable') {
                return '/all-your-accounts';
            }
            if (currentRoute.name === 'DataProtectionScreen') {
                return '/fraud-detection';
            }
            if (currentRoute.name === 'FraudProtectionIntro') {
                return '/fraud-detection-intro';
            }
            if (currentRoute.name === 'BreachScreen') {
                return '/fraud-detection/breach';
            }
            if (currentRoute.name === 'DataProtectionSettings') {
                return '/fraud-detection-settings';
            }
            if (currentRoute.name === 'PhoneFieldPage') {
                return '/phone-field';
            }
            if (currentRoute.name === 'PhonePrefixes') {
                return '/phone-prefixes';
            }
            if (currentRoute.name === 'TextFieldPage') {
                return '/text-field';
            }
            if (currentRoute.name === 'ExportDataLiveSuccess') {
                return '/export-data-live/success';
            }
            if (currentRoute.name === 'DummyAccountImportTransactions') {
                return '/offline-accounts/import-transactions';
            }
            if (currentRoute.name === 'DummyAccountImportAddTransactions') {
                return '/offline-accounts/import-add-transactions';
            }
            if (currentRoute.name === 'DummyAccountImportCurrency') {
                return '/offline-accounts/import-currency';
            }
            if (currentRoute.name === 'ConnectionAccountsClosed') {
                return '/bank-connection-accounts-closed';
            }
        }
        // Default behavior for all other cases
        return defaultGetPathFromState(state, options);
    },
};
const WebRoot = () => {
    const handleNavStateChange = (navigationState, initRoute) => {
        const route = initRoute || navigationState?.routes[navigationState?.index];
        if (route) {
            const { routeName, routeParams } = (() => {
                if (route.state) {
                    return route.state.index && route.state.routes
                        ? {
                            routeName: route.state.routes[route.state.index].name,
                            routeParams: route.state.routes[route.state.index].params,
                        }
                        : { routeName: route.name, routeParams: route.params };
                }
                return { routeName: route.name, routeParams: route.params };
            })();
            store.dispatch(setCurrentRouteName(routeName));
            if (__DEV__) {
                const msg = `CURRENT ROUTE: ${routeName}`;
                log('====================ROUTE===CHANGE===START====================', false, 'blue');
                log(msg, false, 'blue');
                log(routeParams, true, 'blue');
                log('=====================ROUTE===CHANGE===END=====================', false, 'blue');
            }
        }
    };
    return (<Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <SafeAreaProvider>
          <KeyboardProvider>
            <NavigationContainer linking={linking} ref={navigationRef} documentTitle={{
            formatter: (options, route) => getDocumentTitle(options, route?.name),
        }} onReady={() => handleNavStateChange(undefined, navigationRef.getCurrentRoute())} onStateChange={handleNavStateChange}>
              <View style={styles.outer}>
                <StatusBarScrambled />
                <View style={styles.main}>
                  <AuthRedirectWrapper />
                  <WebNavigationBar />
                  <WebStack />
                </View>
                <SpaceUnavailableLock />
                <PopUpMenuModal />
                <Modal />
                <Modal top/>
                <AlertModalWeb />
                <ErrorModal />
                <CreditReportUntrackedToast />
                <Modal top/>
                <ReducedMotionConfig mode={ReduceMotion.Never}/>
              </View>
            </NavigationContainer>
          </KeyboardProvider>
        </SafeAreaProvider>
      </PersistGate>
    </Provider>);
};
const styles = StyleSheet.create({
    main: {
        flex: 1,
        flexDirection: 'row',
    },
    outer: {
        flex: 1,
    },
});
export default WebRoot;
